<template>
  <pui-dialog
    ref="dialog"
    :title="dialogTitle"
    :showCloseButton="false"
    @close="onClose"
    class="send-poster-summary-dialog send-poster-summary-dialog__root"
    transition="slide-bottom"
    width="100%"
    fill
  >
    <template #dialog-action>
      <div class="dialog-action">
        <template v-if="showFormError || showGenericError">
          <pui-button @click="onClose" outline variant="secondary">
            {{ $t('poster.send_summary_dialog.buttons.close') }}
          </pui-button>
        </template>
        <template v-else>
          <pui-button @click="onClose" outline variant="secondary">
            {{ $t('poster.send_summary_dialog.buttons.cancel') }}
          </pui-button>
          <pui-button
            @click="onConfirm"
            :disabled="!areAttributesValid"
            class="btn-download-confirm"
            variant="primary"
          >
            <template v-if="currentAction === CreationActions.CREATE">
              {{ $t('poster.send_summary_dialog.buttons.confirm.create') }}
            </template>
            <template v-else-if="currentAction === CreationActions.DOWNLOAD">
              {{ $t('poster.send_summary_dialog.buttons.confirm.download') }}
            </template>
            <template v-else-if="currentAction === CreationActions.PRINT_AUTO">
              {{ $t('poster.send_summary_dialog.buttons.confirm.print_auto') }}
            </template>
          </pui-button>
        </template>
      </div>
    </template>

    <template #default>
      <template v-if="showFormError">
        <pui-flex
          justifyContent="center"
          class="send-poster-summary-dialog__configuration-error"
          flex="1"
        >
          <i class="mdi mdi-close-circle-outline error-icon"> </i>
          <pui-flex direction="column" class="message-container">
            <i18n
              path="poster.send_summary_dialog.error.configuration.primary"
              class="error-message"
            ></i18n>
            <i18n
              path="poster.send_summary_dialog.error.configuration.secondary"
              class="error-message"
            ></i18n>
          </pui-flex>
        </pui-flex>
      </template>
      <slot v-else-if="showGenericError" name="genericError"></slot>

      <template v-else>
        <pui-flex direction="column" flex="70%">
          <template v-if="loadingPreviews">
            <pui-spinner class="previews-spinner" position="fixed"></pui-spinner>
          </template>
          <template v-else>
            <pui-flex
              v-if="totalPosterCount > 0"
              flex="1"
              justifyContent="center"
              class="send-poster-summary-dialog__pagination-container"
            >
              <pui-pagination
                :total="totalPosterCount"
                :pageSize="pageSize"
                :value="pageIndex + 1"
                @change="onPageIndexChanged"
                pageMode="determinate"
                class="send-poster-summary-dialog__pagination"
              />
            </pui-flex>

            <div v-scrollable.y class="send-poster-summary-dialog__previews-container">
              <div
                v-for="preview in previews"
                :key="preview.id"
                class="send-poster-summary-dialog__preview-item"
              >
                <pui-aspect-ratio aspectRatio="5:3">
                  <pui-flex class="preview-img" justifyContent="center" alignItems="center">
                    <slot
                      :preview="preview"
                      :showAllPrintables="showAllPrintables"
                      name="posterPreview"
                    >
                    </slot>
                  </pui-flex>
                </pui-aspect-ratio>
                <div class="preview-information">
                  <slot :preview="preview" name="previewInformation"></slot>
                </div>
              </div>
            </div>
          </template>
        </pui-flex>

        <pui-flex class="send-poster-summary-dialog__meta" direction="column">
          <pui-flex direction="column" class="step1">
            <pui-flex class="total-count" alignItems="center">
              <i
                :class="{
                  'mdi mdi-download': currentAction === CreationActions.DOWNLOAD,
                  'mdi mdi-send': currentAction !== CreationActions.DOWNLOAD,
                  'count-action-icon': true,
                }"
              />
              <span>{{ countLabel }}</span>
            </pui-flex>
          </pui-flex>

          <pui-flex
            v-if="form && form.attributeGroups && values"
            class="send-poster-summary-dialog__attributes-wrapper step2"
            flex="1"
            direction="column"
          >
            <pui-attribute-group-panel
              v-for="group in form.attributeGroups"
              :key="group.itemId"
              :group="group"
            >
              <template v-if="group.attributes">
                <pui-attribute-panel
                  v-for="attribute in group.attributes"
                  :ref="`formAttribute_${attribute.alias}`"
                  :key="attribute.itemId"
                  :attribute="attribute"
                  :value="values[attribute.itemId] ? values[attribute.itemId].value : undefined"
                  :loadOptionsFunction="loadOptionsFunction"
                  @valueInput="onValueInput(attribute, $event)"
                  @valueChanged="onValueChanged(attribute, $event)"
                  :importExternalFiles="importExternalFiles"
                />
              </template>
            </pui-attribute-group-panel>
          </pui-flex>
        </pui-flex>
      </template>
    </template>
  </pui-dialog>
</template>

<script>
import Vue from 'vue';

import services from '../../../core/services';
import { AttributeTypes } from '../../common/constants';
import { areAllAttributesValid } from '../../common/helpers/attributeValidators';
import { getAttributeDefaultValue, iterateForm, updateOn } from '../../common/helpers/formsHelper';
import { AttributeAlias, CreationActions } from '../constants';
import { getPosterService } from '../services';

export default {
  name: 'pui-send-poster-summary-dialog',
  props: {
    /**
     * The current creation action
     */
    currentAction: {
      type: null,
      required: true,
    },
    /**
     * Generates the previews to display
     * @type {(pageIndex: number, pageSize: number) => Promise<object[]>}
     */
    generatePreviews: {
      type: Function,
      required: true,
    },
    /**
     * If the 'genericError' slot should be rendered instead
     * of previews
     */
    showGenericError: {
      type: Boolean,
      default: false,
    },
    /**
     * Number of previews to show per page
     */
    pageSize: {
      type: Number,
      default: 9,
    },
  },
  data() {
    return {
      CreationActions,
      importExternalFileCount: 0,
      areAttributesValid: true,

      loadingPreviews: true,
      previews: [],
      pageIndex: 0,
      totalPosterCount: 0,
      showFormError: false,

      form: null,
      /**
       * Maps an attribute's itemId to a value object
       * @type {{[attributeItemId: string]: {attributeId: string; attributeAlias: string, type: string; value: *}}}
       */
      values: {},
    };
  },
  computed: {
    /**
     * @returns {string} title for the dialog
     */
    dialogTitle() {
      switch (this.currentAction) {
        case CreationActions.CREATE:
          return this.$t('poster.send_summary_dialog.title.create');
        case CreationActions.DOWNLOAD:
          return this.$t('poster.send_summary_dialog.title.download');
        case CreationActions.PRINT_AUTO:
          return this.$t('poster.send_summary_dialog.title.print_auto');
        default:
          return '';
      }
    },
    /**
     * @returns the label for the total count of posters
     */
    countLabel() {
      switch (this.currentAction) {
        case CreationActions.CREATE:
          return this.$tc('poster.send_summary_dialog.create_count', this.totalPosterCount, {
            count: this.totalPosterCount,
          });
        case CreationActions.DOWNLOAD:
          return this.$tc('poster.send_summary_dialog.download_count', this.totalPosterCount, {
            count: this.totalPosterCount,
          });
        case CreationActions.PRINT_AUTO:
          return this.$tc('poster.send_summary_dialog.print_auto_count', this.totalPosterCount, {
            count: this.totalPosterCount,
          });
        default:
          return '';
      }
    },
    /**
     * @returns {boolean} the showAllPrintables value from our form values.
     * Note that the value is added by default even if there is no form, or no
     * corresponding form attribute
     */
    showAllPrintables() {
      let value = false;

      for (const attrValue of this.valuesToArray()) {
        if (attrValue.attributeAlias === AttributeAlias.SHOW_ALL_PRINTABLES) {
          value = attrValue.value;
          break;
        }
      }
      return value;
    },
  },
  methods: {
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    /**
     * Opens the dialog
     */
    async open({ form, showFormError, defaultShowAllPrintables, totalPosterCount }) {
      this.$refs.dialog.open();

      this.resetState();
      this.showFormError = showFormError;
      this.form = form;
      this.totalPosterCount = totalPosterCount;
      if (!this.showFormError && !this.showGenericError) {
        await this.$nextTick();
        this.initValues(defaultShowAllPrintables);
        this.previews = await this.generatePreviews(this.pageIndex, this.pageSize);
      }
      this.loadingPreviews = false;
    },
    /**
     * Emits the 'creationConfirmed' event
     */
    onConfirm() {
      if (this.importExternalFileCount) {
        services
          .getService('alerts')
          .alertWarning(this.$t('poster.creation.warning.import_external_file_in_progress'));
        return;
      }

      this.checkFormIsValid();
      if (!this.areAttributesValid) {
        services
          .getService('alerts')
          .alertWarning(this.$t('poster.creation.warning.correct_errors'));
        return false;
      }

      this.$emit('creationConfirmed', {
        parameters: { values: this.valuesToArray() },
      });
    },
    /**
     * Handles the dialog closed event
     */
    onClose() {
      this.$refs.dialog.close();

      this.resetState();

      this.$emit('closed');
    },
    /**
     * Closes the dialog without triggering the 'closed' event
     */
    closeSilently() {
      this.$refs.dialog.close();
      this.resetState();
    },
    /**
     * Resets the dialog state
     */
    resetState() {
      this.importExternalFileCount = 0;
      this.areAttributesValid = true;

      this.loadingPreviews = true;
      this.previews = [];
      this.pageIndex = 0;
      this.totalPosterCount = 0;

      this.form = null;
      this.showFormError = false;
      this.values = {};
    },
    /**
     * Pagination change callback
     * @param {number} - the new page number (not index)
     */
    async onPageIndexChanged(pageNumber) {
      this.pageIndex = pageNumber - 1;
      this.previews = await this.generatePreviews(this.pageIndex, this.pageSize);
    },
    // FORM
    /**
     * Initializes the values object with the form's
     * default values
     *
     * @param {boolean} defaultShowAllPrintables - default value of showAllPrintables
     * if the form doesn't have the corresponding attribute
     */
    initValues(defaultShowAllPrintables) {
      let didSetShowAllPrintables = false;

      // Clear any existing values
      this.values = {};

      iterateForm(this.form, attribute => {
        const defaultValue = getAttributeDefaultValue(attribute);
        this.$set(this.values, attribute.itemId, {
          attributeId: attribute.itemId,
          attributeAlias: attribute.alias,
          type: attribute.type,
          value: defaultValue,
        });
        didSetShowAllPrintables =
          didSetShowAllPrintables || attribute.alias === AttributeAlias.SHOW_ALL_PRINTABLES;
      });

      // Add default showAllPrintables if needed
      if (!didSetShowAllPrintables) {
        // Values are supposed to be mapped by itemId, but since we're adding
        // the default value because the attribute doesn't exist, it doesn't matter
        this.$set(this.values, AttributeAlias.SHOW_ALL_PRINTABLES, {
          attributeAlias: AttributeAlias.SHOW_ALL_PRINTABLES,
          value: defaultShowAllPrintables,
        });
      }
    },
    /**
     * @returns {{attributeId: string; attributeAlias: string, type: string; value: *}[]} the values
     * as an array of attribute value objects
     */
    valuesToArray() {
      return Object.values(this.values);
    },
    /**
     * Checks if the form is valid and stores the result in 'this.areAttributesValid'
     */
    checkFormIsValid() {
      const checkResult = areAllAttributesValid(this.values, this.form, {
        validateInvisibleItems: false,
        breakOnFirstError: true,
        attributeIdProp: 'itemId',
        getAttributeValue: (values, attribute) =>
          values[attribute.itemId] ? values[attribute.itemId].value : undefined,
      });
      const { errors } = checkResult;
      this.areAttributesValid = Object.keys(errors).length === 0;
    },
    /**
     * Updates attribute value
     *
     * @param {object} attribute - the attribute whose value changed
     * @param {Object} newValue - New attribute value
     */
    onValueInput(attribute, value) {
      if (this.values[attribute.id]) {
        this.values[attribute.id].value = value;
      } else {
        this.$set(this.values, attribute.itemId, {
          attributeId: attribute.itemId,
          attributeAlias: attribute.alias,
          type: attribute.type,
          value: value,
        });
      }
    },
    /**
     * Updates attribute value and checks form
     *
     * @param {object} attribute - the attribute whose value changed
     * @param {Object} newValue - New attribute value
     */
    onValueChanged(attribute, value) {
      this.onValueInput(attribute, value);
      this.startUpdateOn(attribute);
      this.checkFormIsValid();
    },
    /**
     * Launches the "updateOn" mechanism for the attribute
     *
     * @param {object} attribute - the attribute whose updateOn to trigger
     */
    startUpdateOn(attribute) {
      const actionCb = cbAttr => {
        if (cbAttr.type === AttributeTypes.LINKS) {
          this.$nextTick(() => {
            const attributePanel = this.$refs[`formAttribute_${cbAttr.alias}`];
            if (attributePanel != null && attributePanel[0] != null) {
              attributePanel[0].loadOptions().catch(() => null);
            }
          });
        }
      };

      updateOn(this.currentForm, attribute, actionCb);
    },
    /**
     * Imports external files to the attribute's storage
     *
     * @param {object} attribute - the attribute onto which we are uploading the files
     * @param {object[]} tempFiles - array of objects containing the file blobs to upload
     * @param {(tempFile, importedFile) => void} onFileUploadedCb - callback for when a file is finished uploading.
     * Receives the original temp file, and the uploaded file
     * @param {(cancelCb: (tempId: string) => void) => void} provideAbortCb - callback
     * that proves the cancel function
     */
    async importExternalFiles(attribute, tempFiles, onFileUploadedCb, provideAbortCb) {
      this.importExternalFileCount++;

      const importedFiles = await getPosterService('posterAttributes').importExternalFiles(
        attribute,
        tempFiles,
        onFileUploadedCb,
        provideAbortCb
      );

      this.importExternalFileCount--;
      return importedFiles;
    },
    /**
     * @param {object} attribute - the attribute whose options to retrieve
     * @param {object} parameters - search parameters
     * @param {object} parameters.context - context to send with the search
     * @returns {Promise<object[]>} options for the attribute
     */
    loadOptionsFunction(attribute, { context }) {
      return getPosterService('values').getPossibleValues(
        attribute,
        context,
        null,
        bodyParameters =>
          getPosterService('values').addAttributeValuesBodyParameters(
            bodyParameters,
            this.values,
            this.form.attributeGroups
          )
      );
    },
  },
};
</script>
